import { useAppSelector, useAppDispatch } from 'universal-scripts'
import { closeItemEditor } from 'src/store/slices/dashboard'
import type { ItemEditorState } from 'src/store/slices/dashboard'
import type { ChartType, ValueOverride } from 'src/types/dashboard'
import LabelForm from './LabelForm/LabelForm'
import ChartForm from './ChartForm/ChartForm'
import ImageForm from './ImageForm/ImageForm'

import './ItemEditor.css'

interface ItemEditorInnerProps {
  editor: ItemEditorState
}

const ItemEditorInner = ({ editor }: ItemEditorInnerProps) => {
  const item = useAppSelector((state) =>
    editor.itemId
      ? state.dashboard.draft.items?.find((i) => i.itemId === editor.itemId)
      : null
  )

  if (editor.type === 'label') {
    const initial =
      item?.type === 'label'
        ? { title: item.title, text: item.text }
        : { title: '', text: '' }
    return (
      <LabelForm
        initial={initial}
        itemId={editor.itemId}
        existingWidth={item?.width}
      />
    )
  }

  if (editor.type === 'image') {
    const initial =
      item?.type === 'image'
        ? { title: item.title, image: item.image }
        : { title: '', image: '' }
    return (
      <ImageForm
        initial={initial}
        itemId={editor.itemId}
        existingWidth={item?.width}
      />
    )
  }

  if (editor.type === 'chart') {
    if (
      item &&
      item.type !== 'label' &&
      item.type !== 'image' &&
      item.type !== 'spacer'
    ) {
      return (
        <ChartForm
          initial={{
            type: item.type,
            title: item.title,
            description: item.description,
            xAxis: item.xAxis,
            yAxis: item.yAxis,
            geoColumn: item.geoColumn,
            overrides: item.overrides
          }}
          itemId={editor.itemId}
          query={item.query}
          existingWidth={item.width}
        />
      )
    }

    const initial: {
      type: ChartType
      title: string
      description: string
      xAxis: string[]
      yAxis: string[]
      geoColumn?: string
      overrides?: Record<string, ValueOverride>
    } = {
      type: 'bar-chart',
      title: editor.defaultTitle ?? '',
      description: editor.defaultDescription ?? '',
      xAxis: [],
      yAxis: []
    }
    return (
      <ChartForm initial={initial} itemId={null} query={editor.queryId ?? ''} />
    )
  }

  return null
}

export default function ItemEditor() {
  const editor = useAppSelector((state) => state.dashboard.itemEditor)
  const dispatch = useAppDispatch()

  if (!editor) return null

  const key = `${editor.type}-${editor.itemId ?? 'new'}`

  return (
    <>
      <ItemEditorInner key={key} editor={editor} />
      <div
        className="item-editor-scrim"
        onClick={() => dispatch(closeItemEditor())}
      />
    </>
  )
}
